
import { Component, Vue } from 'vue-property-decorator';
import OsCampaignForm from '@/components/shared/campaigns/OsCampaignForm.vue';
import {
  ICampaign,
  ILink,
  IRecipient,
  IReportContentCategory,
  Recipientgroup,
  ReportAudience
} from '@/models/application';
import OsSheet from '@/components/core/sheet/OsSheet.vue';
import OsSheetContent from '@/components/core/sheet/OsSheetContent.vue';
import { customersApi } from '@/apis';
import OsLoadingIndicator from '@/components/core/OsLoadingIndicator.vue';
import OsAntesDisc from '@/components/chart/OsAntesDisc.vue';
import OsLinksOverview from '@/components/shared/links/OsLinksOverview.vue';
import OsRecipientsOverview from '@/components/shared/recipients/OsRecipientsOverview.vue';
import { AppDirtyUtil } from '@/utils/AppDirtyUtil';

@Component({
  components: {
    OsRecipientsOverview,
    OsLinksOverview,
    OsAntesDisc,
    OsLoadingIndicator,
    OsSheetContent,
    OsSheet,
    OsCampaignForm
  }
})
export default class CampaignCreate extends Vue {
  appDirtyUtil: AppDirtyUtil = new AppDirtyUtil();

  campaign: ICampaign | null = null;

  showCreateDialog = false;
  loading = false;
  customerId!: string;
  campaignId!: string;

  recipients: IRecipient[] = [];

  removedCategoriesForCompany: IReportContentCategory[] = [];
  addedCategoriesForCompany: IReportContentCategory[] = [];
  removedCategoriesForParticipants: IReportContentCategory[] = [];
  addedCategoriesForParticipants: IReportContentCategory[] = [];

  created() {
    this.customerId = this.$route.params.id;
  }

  async mounted() {
    this.campaignId = this.$route.params.campaignid;
    this.campaign = await customersApi.getCampaignById(
      this.customerId,
      this.campaignId
    );

    this.recipients = await customersApi.getCampaignRecipients(
      this.customerId,
      this.campaignId
    );
  }

  get isRecipientGroupParticipants(): boolean {
    return this.campaign?.recipientgroup === Recipientgroup.Participants;
  }

  async onSave() {
    this.loading = true;
    try {
      this.campaign!.customerId = this.customerId;
      this.campaign = await customersApi.updateCampaign(
        this.customerId,
        this.campaign!
      );

      await this.saveReportContentSegments();

      (this.$refs as any).campaignForm.reloadAssignedCategories();

      this.appDirtyUtil.init([
        this.campaign,
        this.addedCategoriesForCompany,
        this.removedCategoriesForCompany,
        this.addedCategoriesForParticipants,
        this.removedCategoriesForParticipants
      ]);
    } finally {
      this.loading = false;
    }
  }

  async saveReportContentSegments() {
    if (
      this.removedCategoriesForCompany &&
      this.removedCategoriesForCompany.length > 0
    ) {
      for (const c of this.removedCategoriesForCompany) {
        await customersApi.removeReportContentCategoryFromCampaign(
          this.customerId,
          this.campaignId,
          c.id,
          ReportAudience.Company
        );
      }
    }

    if (
      this.addedCategoriesForCompany &&
      this.addedCategoriesForCompany.length > 0
    ) {
      for (const c of this.addedCategoriesForCompany) {
        await customersApi.assignReportContentCategoryToCampaign(
          this.customerId,
          this.campaignId,
          c.id,
          ReportAudience.Company
        );
      }
    }

    if (
      this.removedCategoriesForParticipants &&
      this.removedCategoriesForParticipants.length > 0
    ) {
      for (const c of this.removedCategoriesForParticipants) {
        await customersApi.removeReportContentCategoryFromCampaign(
          this.customerId,
          this.campaignId,
          c.id,
          ReportAudience.Participant
        );
      }
    }

    if (
      this.addedCategoriesForParticipants &&
      this.addedCategoriesForParticipants.length > 0
    ) {
      for (const c of this.addedCategoriesForParticipants) {
        await customersApi.assignReportContentCategoryToCampaign(
          this.customerId,
          this.campaignId,
          c.id,
          ReportAudience.Participant
        );
      }
    }
  }

  async onDelete() {
    if (
      await this.$global.dialogs.confirm(
        'Confirm deletion',
        `Are you sure you want to delete the campaign "${
          this.campaign!.title
        }"?<br><br>The Following data will be deleted.<br> <ul><li>Links</li><li>Recipients</li><li>Participants</li><li>Target profiles</li></ul>`
      )
    ) {
      const result = await customersApi.deleteCampaignById(
        this.customerId,
        this.campaign!.id
      );
      if (result) {
        await this.$router.push(`/customers/${this.customerId}/campaigns/`);
      }
    }
  }

  undoClicked() {
    this.campaign = this.appDirtyUtil.getOriginalObject()[0];
    this.addedCategoriesForCompany = this.appDirtyUtil.getOriginalObject()[1];
    this.removedCategoriesForCompany = this.appDirtyUtil.getOriginalObject()[2];
    this.addedCategoriesForParticipants =
      this.appDirtyUtil.getOriginalObject()[3];
    this.removedCategoriesForParticipants =
      this.appDirtyUtil.getOriginalObject()[4];
    this.appDirtyUtil.init([
      this.campaign,
      this.addedCategoriesForCompany,
      this.removedCategoriesForCompany,
      this.addedCategoriesForParticipants,
      this.removedCategoriesForParticipants
    ]);

    (this.$refs as any).campaignForm.reloadAssignedCategories();
  }

  onRecipientsCreate() {
    this.showCreateDialog = true;
  }

  onRecipientCreated(recipient: IRecipient) {
    this.recipients.push(recipient);
    this.showCreateDialog = false;
  }

  async deleteClicked(recipient: IRecipient) {
    if (
      await this.$global.dialogs.confirm(
        'Confirm deletion',
        `Are you sure you want to delete the selected recipient with the e-mail-address '${recipient.emailaddress}'?`
      )
    ) {
      await this.deleteRecipient(recipient);
    }
  }

  async deleteRecipient(recipient: IRecipient) {
    const result = await customersApi.deleteCampaignRecipient(
      this.customerId,
      this.campaignId,
      recipient.id
    );

    if (result) {
      const index = this.recipients.findIndex((r) => r.id === recipient.id);
      this.recipients.splice(index, 1);

      this.$global.dialogs.toast('Recipient successfully deleted.', {
        color: 'success',
        icon: 'mdi-delete-outline'
      });
    } else {
      this.$global.dialogs.toast('Error while deleting recipient.', {
        color: 'error',
        icon: 'mdi-delete-outline'
      });
    }
  }

  onTypeClicked(link: ILink) {
    if (link.participantId) {
      this.$router.push(
        `/customers/${this.customerId}/campaigns/${this.campaignId}/participants/${link.participantId}`
      );
    }
  }

  goToParticipant(id: string) {
    this.$router.push(
      `/customers/${this.customerId}/campaigns/${this.campaignId}/participants/${id}`
    );
  }
}
